import { useState, type FormEvent, type KeyboardEvent } from "react";
import { SendHorizontal } from "lucide-react";

import { canSubmitAgentPrompt, submitAgentPrompt } from "../../agent-updates";
import type { AgentPromptCommandRecord } from "../../../../../shared/agent";
import { Button } from "@workanywhere/ui";
import { Textarea } from "@workanywhere/ui";

export function PromptComposer({
  agentApiBaseUrl,
  currentTaskId,
  onPromptSubmitted,
  workspaceKey,
}: {
  agentApiBaseUrl: string;
  currentTaskId: string | null;
  onPromptSubmitted: (command: AgentPromptCommandRecord) => void;
  workspaceKey: string;
}) {
  const [prompt, setPrompt] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trimmedPrompt = prompt.trim();
  const canSubmit = canSubmitAgentPrompt({ currentTaskId, prompt: trimmedPrompt, isSubmitting });

  async function handleSubmit(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      if (!currentTaskId) {
        throw new Error("Open a task before sending a prompt");
      }

      const command = await submitAgentPrompt({ agentApiBaseUrl, workspaceKey, taskId: currentTaskId, prompt: trimmedPrompt });
      onPromptSubmitted(command);
      setPrompt("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    void handleSubmit();
  }

  return (
    <form className="shrink-0 border-t bg-card/80 p-3" onSubmit={handleSubmit}>
      <div className="flex items-end gap-2 rounded-lg border bg-background p-2 shadow-sm">
        <Textarea
          aria-label="Prompt"
          className="max-h-32 min-h-11 resize-none border-0 bg-transparent px-2 py-2 shadow-none focus-visible:ring-0"
          placeholder={currentTaskId ? "Message the agent" : "Select a task before sending"}
          value={prompt}
          disabled={isSubmitting || !currentTaskId}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button type="submit" size="icon" disabled={!canSubmit} aria-label="Send prompt">
          <SendHorizontal className="size-4" />
        </Button>
      </div>
      {errorMessage ? <p className="mt-2 text-xs text-destructive">{errorMessage}</p> : null}
    </form>
  );
}
